/**
 * Configuration Form for Smart Fog Schematic
 * Collapsible sidebar with organized sections
 */

import { useState, useCallback, useEffect } from 'react';

export interface ZoneConfig {
  unitCount: number;
  /** Model variant: 1 = 6 L/h, 2 = 12 L/h */
  modelVariant: 1 | 2;
  unitModel: 'TS100-1' | 'TS100-2' | 'ES100-1' | 'ES100-2';
}

export interface ControllerConfig {
  model: 'CPLC1' | 'CPLC3' | 'CPLC6';
  systemType: 'in-room' | 'in-duct';
  zones: ZoneConfig[];
}

export interface AreaConfig {
  areaLabel: string;
  ductWidth: number;
  ductHeight: number;
  ductDepth: number;
  unitModel: 'TS100-1' | 'TS100-2' | 'ES100-1' | 'ES100-2';
  nozzleSpacing: number[];
}

export interface SchematicConfig {
  drawingNumber: string;
  projectName: string;
  // Water section
  zeroTdsCapacity: 350 | 1000 | 2000 | 4000 | 6000 | 10000;
  hasCentralUnit: boolean;
  // Pressure tank configuration
  pressureTankCapacity: 20 | 40 | 60 | 80 | 120;
  pressureTankCount: number; // 1-4
  // Compressed Air section
  compressorCapacity: 900 | 2500 | 6000;
  // Control section
  controllers: ControllerConfig[];
  hasBacnet: boolean;
  // Legacy fields for compatibility
  controllerModel: 'CPLC1' | 'CPLC3' | 'CPLC6';
  zoneCount: 1 | 2 | 3 | 6;
  areas: AreaConfig[];
  titleBlock: {
    title: string;
    scale: string;
    drawn: string;
    checked: string;
    engAppr: string;
    version: string;
  };
}

interface ConfigFormProps {
  config: SchematicConfig;
  onChange: (config: SchematicConfig) => void;
  onGenerate: () => void;
  onDownloadPdf: () => void;
  isGenerating: boolean;
}

const ZERO_TDS_OPTIONS = [350, 1000, 2000, 4000, 6000, 10000] as const;
const COMPRESSOR_OPTIONS = [900, 2500, 6000] as const;
const PRESSURE_TANK_CAPACITIES = [20, 40, 60, 80, 120] as const;

const CONTROLLER_SPECS = {
  CPLC1: { zones: 1, label: 'CPLC1' },
  CPLC3: { zones: 3, label: 'CPLC3' },
  CPLC6: { zones: 6, label: 'CPLC6' }
} as const;

const DUCT_PRESETS = [
  { width: 22, nozzles: [3, 3, 4, 3, 4, 3, 3] },
  { width: 28, nozzles: [5, 3, 4, 3, 4, 3, 5] },
  { width: 30, nozzles: [4, 4, 4, 4, 4, 4, 4] },
  { width: 38, nozzles: [5, 4, 5, 4, 5, 4, 5] },
  { width: 40, nozzles: [5, 5, 5, 5, 5, 5, 5] }
];

// Default zone config based on system type
function createDefaultZones(count: number, systemType: 'in-room' | 'in-duct'): ZoneConfig[] {
  const unitModel = systemType === 'in-duct' ? 'TS100-2' : 'ES100-2';
  return Array.from({ length: count }, () => ({
    unitCount: 1,
    modelVariant: 2 as const,  // Default to model 2 (12 L/h)
    unitModel
  }));
}

interface SectionProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Section({ title, icon, children, defaultOpen = true }: SectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="config-section">
      <button
        className="config-section-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="config-section-icon">{icon}</span>
        <span className="config-section-title">{title}</span>
        <span className="config-section-toggle">{isOpen ? '−' : '+'}</span>
      </button>
      {isOpen && <div className="config-section-body">{children}</div>}
    </div>
  );
}

export function ConfigForm({
  config,
  onChange,
  onGenerate,
  onDownloadPdf,
  isGenerating
}: ConfigFormProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedAreaIndex, setSelectedAreaIndex] = useState(0);
  const [expandedController, setExpandedController] = useState<number | null>(0);

  const updateConfig = useCallback(
    (updates: Partial<SchematicConfig>) => {
      onChange({ ...config, ...updates });
    },
    [config, onChange]
  );

  // Auto-generate on config change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      onGenerate();
    }, 500);
    return () => clearTimeout(timer);
  }, [config.zeroTdsCapacity, config.compressorCapacity, config.controllers, config.hasBacnet, config.hasCentralUnit, config.pressureTankCapacity, config.pressureTankCount]);

  const updateController = useCallback(
    (index: number, updates: Partial<ControllerConfig>) => {
      const newControllers = [...(config.controllers || [])];
      const currentController = newControllers[index];

      // If model changes, update zones count
      if (updates.model && updates.model !== currentController.model) {
        const newZoneCount = CONTROLLER_SPECS[updates.model].zones;
        const systemType = updates.systemType || currentController.systemType;
        updates.zones = createDefaultZones(newZoneCount, systemType);
      }

      // If system type changes, update unit models in zones
      if (updates.systemType && updates.systemType !== currentController.systemType) {
        updates.zones = (currentController.zones || []).map(z => {
          const variant = z.modelVariant || 2;
          const unitModel = updates.systemType === 'in-duct'
            ? (variant === 1 ? 'TS100-1' : 'TS100-2')
            : (variant === 1 ? 'ES100-1' : 'ES100-2');
          return {
            ...z,
            unitModel: unitModel as ZoneConfig['unitModel']
          };
        });
      }

      newControllers[index] = { ...currentController, ...updates };

      // Update legacy fields based on first controller
      const firstController = newControllers[0];
      updateConfig({
        controllers: newControllers,
        controllerModel: firstController?.model || 'CPLC3',
        zoneCount: (firstController ? CONTROLLER_SPECS[firstController.model].zones : 3) as 1 | 2 | 3 | 6
      });
    },
    [config.controllers, updateConfig]
  );

  const updateZone = useCallback(
    (controllerIndex: number, zoneIndex: number, updates: Partial<ZoneConfig>) => {
      const newControllers = [...(config.controllers || [])];
      const controller = newControllers[controllerIndex];
      const newZones = [...(controller.zones || [])];
      newZones[zoneIndex] = { ...newZones[zoneIndex], ...updates };
      newControllers[controllerIndex] = { ...controller, zones: newZones };
      updateConfig({ controllers: newControllers });
    },
    [config.controllers, updateConfig]
  );

  const addController = useCallback(() => {
    const newController: ControllerConfig = {
      model: 'CPLC3',
      systemType: 'in-duct',
      zones: createDefaultZones(3, 'in-duct')
    };
    const newControllers = [...(config.controllers || []), newController];
    updateConfig({ controllers: newControllers });
    setExpandedController(newControllers.length - 1);
  }, [config.controllers, updateConfig]);

  const removeController = useCallback(
    (index: number) => {
      if ((config.controllers || []).length <= 1) return;
      const newControllers = (config.controllers || []).filter((_, i) => i !== index);
      updateConfig({ controllers: newControllers });
      if (expandedController === index) {
        setExpandedController(null);
      } else if (expandedController !== null && expandedController > index) {
        setExpandedController(expandedController - 1);
      }
    },
    [config.controllers, expandedController, updateConfig]
  );

  const updateArea = useCallback(
    (index: number, updates: Partial<AreaConfig>) => {
      const newAreas = [...config.areas];
      newAreas[index] = { ...newAreas[index], ...updates };
      updateConfig({ areas: newAreas });
    },
    [config.areas, updateConfig]
  );

  const addArea = useCallback(() => {
    const preset = DUCT_PRESETS[1];
    updateConfig({
      areas: [
        ...config.areas,
        {
          areaLabel: `Area ${config.areas.length + 1}`,
          ductWidth: preset.width,
          ductHeight: 6,
          ductDepth: 14,
          unitModel: 'TS100-2',
          nozzleSpacing: preset.nozzles
        }
      ]
    });
    setSelectedAreaIndex(config.areas.length);
  }, [config.areas, updateConfig]);

  const removeArea = useCallback(
    (index: number) => {
      if (config.areas.length <= 1) return;
      const newAreas = config.areas.filter((_, i) => i !== index);
      updateConfig({ areas: newAreas });
      setSelectedAreaIndex(Math.min(selectedAreaIndex, newAreas.length - 1));
    },
    [config.areas, selectedAreaIndex, updateConfig]
  );

  const handleDuctWidthChange = useCallback(
    (index: number, width: number) => {
      const preset = DUCT_PRESETS.find(p => p.width === width);
      if (preset) {
        updateArea(index, { ductWidth: width, nozzleSpacing: preset.nozzles });
      } else {
        updateArea(index, { ductWidth: width });
      }
    },
    [updateArea]
  );

  // Initialize controllers if not present or missing zones
  const controllers: ControllerConfig[] = (config.controllers || []).map(c => ({
    ...c,
    zones: c.zones || createDefaultZones(CONTROLLER_SPECS[c.model].zones, c.systemType)
  }));

  if (controllers.length === 0) {
    controllers.push({
      model: 'CPLC3',
      systemType: 'in-duct',
      zones: createDefaultZones(3, 'in-duct')
    });
  }

  const selectedArea = config.areas[selectedAreaIndex];

  // Calculate totals
  const totalZones = controllers.reduce((sum, c) => sum + (c.zones?.length || CONTROLLER_SPECS[c.model].zones), 0);
  const totalUnits = controllers.reduce((sum, c) =>
    sum + (c.zones || []).reduce((zSum, z) => zSum + z.unitCount, 0), 0);

  if (isCollapsed) {
    return (
      <div className="config-panel-collapsed">
        <button
          className="config-toggle-btn"
          onClick={() => setIsCollapsed(false)}
          title="Show Configuration"
        >
          <span className="config-toggle-icon">☰</span>
          <span className="config-toggle-label">Config</span>
        </button>
      </div>
    );
  }

  return (
    <div className="config-panel-floating">
      <div className="config-panel-header">
        <span>System Configuration</span>
        <button
          className="config-collapse-btn"
          onClick={() => setIsCollapsed(true)}
          title="Hide Panel"
        >
          ✕
        </button>
      </div>

      <div className="config-panel-content">
        {/* Project Info */}
        <Section title="Project" icon="📋" defaultOpen={false}>
          <div className="form-group">
            <label className="form-label">Drawing Number</label>
            <input
              type="text"
              className="form-input"
              value={config.drawingNumber}
              onChange={e => updateConfig({ drawingNumber: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Project Name</label>
            <input
              type="text"
              className="form-input"
              value={config.projectName}
              onChange={e => updateConfig({ projectName: e.target.value })}
            />
          </div>
        </Section>

        {/* Water Section */}
        <Section title="Water" icon="💧">
          <div className="form-group">
            <label className="form-label">ZeroTDS Capacity (GPD)</label>
            <div className="option-grid">
              {ZERO_TDS_OPTIONS.map(capacity => (
                <button
                  key={capacity}
                  className={`option-btn ${config.zeroTdsCapacity === capacity ? 'selected' : ''}`}
                  onClick={() => updateConfig({ zeroTdsCapacity: capacity })}
                >
                  {capacity.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Pressure Tank Capacity (GAL)</label>
            <div className="option-grid">
              {PRESSURE_TANK_CAPACITIES.map(capacity => (
                <button
                  key={capacity}
                  className={`option-btn ${(config.pressureTankCapacity || 20) === capacity ? 'selected' : ''}`}
                  onClick={() => updateConfig({ pressureTankCapacity: capacity })}
                >
                  {capacity}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Tank Count</label>
            <div className="form-row">
              <input
                type="number"
                className="form-input"
                min="1"
                max="4"
                value={config.pressureTankCount || 1}
                onChange={e => updateConfig({ pressureTankCount: Math.max(1, Math.min(4, parseInt(e.target.value) || 1)) })}
              />
              <span className="form-hint" style={{ marginLeft: '0.5rem', color: '#666' }}>1-4 tanks</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label checkbox-label">
              <input
                type="checkbox"
                checked={config.hasCentralUnit ?? false}
                onChange={e => updateConfig({ hasCentralUnit: e.target.checked })}
              />
              <span>Central Unit</span>
            </label>
          </div>
        </Section>

        {/* Compressed Air Section */}
        <Section title="Compressed Air" icon="🌬️">
          <div className="form-group">
            <label className="form-label">Compressor Capacity (SCFM)</label>
            <div className="option-grid option-grid-3">
              {COMPRESSOR_OPTIONS.map(capacity => (
                <button
                  key={capacity}
                  className={`option-btn ${config.compressorCapacity === capacity ? 'selected' : ''}`}
                  onClick={() => updateConfig({ compressorCapacity: capacity })}
                >
                  {capacity.toLocaleString()}
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* Control Section */}
        <Section title="Control" icon="🎛️">
          <div className="config-summary">
            <span>{controllers.length} Controller{controllers.length > 1 ? 's' : ''}</span>
            <span className="summary-dot">•</span>
            <span>{totalZones} Zone{totalZones > 1 ? 's' : ''}</span>
            <span className="summary-dot">•</span>
            <span>{totalUnits} Unit{totalUnits > 1 ? 's' : ''}</span>
          </div>

          <div className="controller-list">
            {controllers.map((controller, cIndex) => (
              <div key={cIndex} className="controller-card">
                <div
                  className="controller-card-header"
                  onClick={() => setExpandedController(expandedController === cIndex ? null : cIndex)}
                >
                  <div className="controller-card-title">
                    <span className="controller-badge">{controller.model}</span>
                    <span className="controller-type">
                      {controller.systemType === 'in-duct' ? 'In-Duct' : 'In-Room'}
                    </span>
                  </div>
                  <div className="controller-card-info">
                    <span>{controller.zones?.length || 0} zones</span>
                    <span className="expand-icon">{expandedController === cIndex ? '▼' : '▶'}</span>
                  </div>
                </div>

                {expandedController === cIndex && (
                  <div className="controller-card-body">
                    <div className="controller-settings">
                      <select
                        className="form-select"
                        value={controller.model}
                        onChange={e => updateController(cIndex, {
                          model: e.target.value as ControllerConfig['model']
                        })}
                      >
                        {Object.entries(CONTROLLER_SPECS).map(([model, spec]) => (
                          <option key={model} value={model}>
                            {spec.label} ({spec.zones} Zone{spec.zones > 1 ? 's' : ''})
                          </option>
                        ))}
                      </select>
                      <select
                        className="form-select"
                        value={controller.systemType}
                        onChange={e => updateController(cIndex, {
                          systemType: e.target.value as ControllerConfig['systemType']
                        })}
                      >
                        <option value="in-duct">In-Duct (TS100)</option>
                        <option value="in-room">In-Room (ES100)</option>
                      </select>
                    </div>

                    <div className="zone-list">
                      {(controller.zones || []).map((zone, zIndex) => {
                        const unitType = controller.systemType === 'in-duct' ? 'TS100' : 'ES100';
                        const variant = zone.modelVariant || 2;
                        return (
                          <div key={zIndex} className="zone-item">
                            <span className="zone-label">Zone {zIndex + 1}</span>
                            <div className="zone-config">
                              <input
                                type="number"
                                className="form-input zone-count-input"
                                min="1"
                                max="20"
                                value={zone.unitCount}
                                onChange={e => updateZone(cIndex, zIndex, {
                                  unitCount: Math.max(1, parseInt(e.target.value) || 1)
                                })}
                              />
                              <span className="zone-unit-label">×</span>
                              <select
                                className="form-select zone-model-select"
                                value={variant}
                                onChange={e => {
                                  const newVariant = parseInt(e.target.value) as 1 | 2;
                                  const newModel = controller.systemType === 'in-duct'
                                    ? (newVariant === 1 ? 'TS100-1' : 'TS100-2')
                                    : (newVariant === 1 ? 'ES100-1' : 'ES100-2');
                                  updateZone(cIndex, zIndex, {
                                    modelVariant: newVariant,
                                    unitModel: newModel as ZoneConfig['unitModel']
                                  });
                                }}
                              >
                                <option value={1}>{unitType}-1 (6 L/h)</option>
                                <option value={2}>{unitType}-2 (12 L/h)</option>
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {controllers.length > 1 && (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => removeController(cIndex)}
                      >
                        Remove Controller
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button className="btn btn-secondary btn-sm btn-full" onClick={addController}>
            + Add Controller
          </button>

          <div className="form-group" style={{ marginTop: '0.75rem' }}>
            <label className="form-label checkbox-label">
              <input
                type="checkbox"
                checked={config.hasBacnet}
                onChange={e => updateConfig({ hasBacnet: e.target.checked })}
              />
              <span>BACnet Enabled</span>
            </label>
          </div>
        </Section>

        {/* Areas Section - for In-Duct configurations */}
        <Section title="Duct Configuration" icon="📐" defaultOpen={false}>
          <div className="form-group">
            <label className="form-label">Areas ({config.areas.length})</label>
            <div className="area-list">
              {config.areas.map((area, index) => (
                <div
                  key={index}
                  className={`area-item ${index === selectedAreaIndex ? 'selected' : ''}`}
                  onClick={() => setSelectedAreaIndex(index)}
                >
                  <div className="area-item-header">
                    <span>{area.areaLabel}</span>
                    {config.areas.length > 1 && (
                      <button
                        className="area-remove"
                        onClick={e => {
                          e.stopPropagation();
                          removeArea(index);
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div className="area-item-details">
                    {area.ductWidth}" × {area.ductHeight}" × {area.ductDepth}"
                  </div>
                </div>
              ))}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={addArea}>
              + Add Area
            </button>
          </div>

          {selectedArea && (
            <div className="area-config">
              <div className="form-group">
                <label className="form-label">Area Label</label>
                <input
                  type="text"
                  className="form-input"
                  value={selectedArea.areaLabel}
                  onChange={e => updateArea(selectedAreaIndex, { areaLabel: e.target.value })}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Width</label>
                  <select
                    className="form-select"
                    value={selectedArea.ductWidth}
                    onChange={e => handleDuctWidthChange(selectedAreaIndex, parseInt(e.target.value))}
                  >
                    {DUCT_PRESETS.map(p => (
                      <option key={p.width} value={p.width}>{p.width}"</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Height</label>
                  <select
                    className="form-select"
                    value={selectedArea.ductHeight}
                    onChange={e => updateArea(selectedAreaIndex, { ductHeight: parseInt(e.target.value) })}
                  >
                    <option value="6">6"</option>
                    <option value="8">8"</option>
                    <option value="10">10"</option>
                    <option value="12">12"</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Depth</label>
                  <select
                    className="form-select"
                    value={selectedArea.ductDepth}
                    onChange={e => updateArea(selectedAreaIndex, { ductDepth: parseInt(e.target.value) })}
                  >
                    <option value="14">14"</option>
                    <option value="16">16"</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </Section>

        {/* Title Block */}
        <Section title="Title Block" icon="📝" defaultOpen={false}>
          <div className="form-group">
            <label className="form-label">Drawn By</label>
            <input
              type="text"
              className="form-input"
              value={config.titleBlock.drawn}
              onChange={e => updateConfig({ titleBlock: { ...config.titleBlock, drawn: e.target.value } })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Checked By</label>
            <input
              type="text"
              className="form-input"
              value={config.titleBlock.checked}
              onChange={e => updateConfig({ titleBlock: { ...config.titleBlock, checked: e.target.value } })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Version</label>
            <input
              type="text"
              className="form-input"
              value={config.titleBlock.version}
              onChange={e => updateConfig({ titleBlock: { ...config.titleBlock, version: e.target.value } })}
            />
          </div>
        </Section>
      </div>

      {/* Actions */}
      <div className="config-panel-actions">
        <button
          className="btn btn-primary"
          onClick={onGenerate}
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating...' : 'Refresh'}
        </button>
        <button
          className="btn btn-success"
          onClick={onDownloadPdf}
          disabled={isGenerating}
        >
          PDF
        </button>
      </div>
    </div>
  );
}
